/**
 * ConnectorCard — directory grid card. Verbatim recipe: card bg, line border,
 * radius r3, shadow-sm→md on hover + translateY(-1px), pad 18, gap 12.
 * Kind badge top-right, 42px avatar, name + class, purpose, chips, status row.
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii, shadows } from '../../theme';
import { ChevronRight } from '../../icons';
import { KindBadge, StatusPill } from '../../components';
import type { DirItem } from './useDirectory';

type Props = { item: DirItem; onOpen: () => void };

export default function ConnectorCard({ item, onOpen }: Props) {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={[styles.card, { boxShadow: hover ? shadows.md : shadows.sm, transform: hover ? [{ translateY: -1 }] : undefined } as any]}
    >
      <View style={styles.kindAbs}>
        <KindBadge label={item.kindLabel} tone={item.kindTone} />
      </View>

      <View style={styles.head}>
        <View style={[styles.avatar, { backgroundColor: item.color } as any]}>
          <Text style={styles.avatarText}>{item.initials}</Text>
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.name}>{item.name}</Text>
          <Text style={styles.cls}>{item.clsLabel} adapter</Text>
        </View>
      </View>

      <Text style={styles.purpose}>{item.purpose}</Text>

      <View style={styles.chips}>
        {item.chips.map((c, idx) => (
          <View key={idx} style={styles.chip}>
            <Text style={styles.chipGroup}>{c.group}</Text>
            <Text style={styles.chipValue}>{c.value}</Text>
          </View>
        ))}
      </View>

      <View style={styles.footer}>
        <StatusPill label={item.statusLabel} tone={item.tone} />
        <ChevronRight size={16} color={colors.ink4} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    padding: 18,
    flexDirection: 'column',
    gap: 12,
    cursor: 'pointer',
    transition: 'box-shadow 180ms cubic-bezier(0.2,0.8,0.2,1), transform 180ms',
  } as any,
  kindAbs: { position: 'absolute', top: 11, right: 11, zIndex: 1 } as any,
  head: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingRight: 56 },
  avatar: { width: 42, height: 42, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: '#fff' },
  name: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  cls: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  purpose: { fontFamily: fontFamily.sans, color: colors.ink2, fontSize: fontSize.md, lineHeight: 19.5, flex: 1, minHeight: 38 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    height: 22, paddingHorizontal: 9, borderRadius: radii.r2, backgroundColor: colors.paper2,
    borderWidth: 1, borderColor: colors.line,
  },
  chipGroup: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4, fontWeight: fontWeight.medium },
  chipValue: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink2, fontWeight: fontWeight.medium },
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
});
